// src/pages/Faq.js
// v2 – clearer public FAQ copy for Play/public website polish

import React from "react";
import { Helmet } from "react-helmet";
import "../styles/Privacy.css";

const Faq = () => {
  return (
    <div className="privacy-page page">
      <Helmet>
        <title>Security & Privacy FAQ - GhrianGhraffy</title>
        <meta
          name="description"
          content="Security and privacy FAQ for GhrianGhraffy — answers for Irish schools and parents about school photo access, review, GDPR, website publishing, and account deletion."
        />
      </Helmet>

      <div className="privacy-card">
        <header className="privacy-header">
          <p className="privacy-kicker">FAQ</p>
          <h1 className="privacy-title">Security &amp; Privacy FAQ</h1>
          <p className="privacy-meta">Last updated: May 29, 2026</p>
        </header>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Who is GhrianGhraffy for?</h2>
          <p>
            GhrianGhraffy is built for schools that want a clearer way to capture,
            review, organise, and share school photos and videos.
          </p>
          <p>
            Principals and authorised staff can use GhrianGhraffy to upload school
            media. Principals keep oversight through review and school-level settings.
            Parents can view approved content that their school has made available.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Can parents download or screenshot photos?</h2>
          <p>
            Like any service that displays photos or videos, screenshots and screen
            recordings cannot be completely prevented at a technical level.
          </p>
          <p>
            GhrianGhraffy reduces risk by avoiding public discovery, using role-based
            access, limiting access to authorised users, and keeping school sharing
            decisions inside a controlled workflow.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Are school photos publicly visible on the internet?</h2>
          <p>
            No. GhrianGhraffy does not provide a public search page for school media,
            and app media is not intended to be exposed through permanent public links.
          </p>
          <p>
            If a school chooses to publish selected approved items on its own website,
            only content enabled for that destination is shown, and media is delivered
            using controlled, time-limited access links.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How is this different from shared folders, email, or chat apps?</h2>
          <p>
            Shared folders, email attachments, and messaging apps can make it hard for a
            school to keep track of where photos have gone, who has access, and whether
            links or copies remain available later.
          </p>
          <p>
            GhrianGhraffy is designed around a school-managed workflow: staff upload,
            Principals review, and parents see approved content through school-scoped
            access.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Do photos get stored on teachers’ phones?</h2>
          <p>
            GhrianGhraffy is designed to reduce long-term storage of school media on
            personal devices. Working copies may exist temporarily during capture,
            preview, upload, retry, or cancellation.
          </p>
          <p>
            Photos or videos already saved in a device’s own camera roll remain under
            that device user’s control.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Who can see our school’s photos?</h2>
          <p>
            Access is role-based and scoped to a school. Principals and authorised staff
            can manage or review school content according to their role. Parents can view
            approved content made available to their school community.
          </p>
          <p>
            Schools remain responsible for deciding what should be captured, approved,
            published, removed, or restricted under their own policies.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">What happens when a staff member leaves?</h2>
          <p>
            A school administrator can revoke staff access. Revoking access prevents the
            former staff member from continuing to use school features, without deleting
            the school’s existing content.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How does GhrianGhraffy support GDPR compliance?</h2>
          <p>
            GhrianGhraffy supports GDPR-aligned school workflows through role-based
            access, review controls, data minimisation, school-scoped visibility,
            deletion and revocation mechanisms, and security safeguards.
          </p>
          <p>
            In many school use cases, the school is the data controller for pupil photos,
            videos, captions, class labels, and related school content. GhrianGhraffy
            generally acts as a data processor for that school-controlled content.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Does GhrianGhraffy sell data or show ads?</h2>
          <p>
            No. GhrianGhraffy does not sell personal data, does not show advertising in
            the app, and does not use school content for advertising.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How does website publishing work?</h2>
          <p>
            If a school chooses to show approved content on its own website, the website
            integration requests school-approved items from GhrianGhraffy through
            controlled server-side access.
          </p>
          <p>
            Website publishing is optional. A school can choose whether website sharing
            is enabled and which approved content is allowed to appear there.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Can schools publish to Instagram?</h2>
          <p>
            Instagram publishing is optional and only applies where a school chooses to
            connect an official Instagram account and publish selected approved content.
          </p>
          <p>
            Schools should make sure they have the appropriate permission or lawful basis
            before publishing pupil photos or videos to external destinations.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How do we request deletion or changes?</h2>
          <p>
            Principals and school administrators can manage school content through their
            school workflow. For account deletion, data deletion, correction requests, or
            privacy questions, contact{" "}
            <a href="mailto:privacy@ghrianghraffy.com">privacy@ghrianghraffy.com</a>.
          </p>
          <p>
            If the request relates to pupil photos, videos, or school-controlled content,
            parents and guardians should normally contact the school first. GhrianGhraffy
            will work with the school where needed.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Faq;